import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, MailCheck, KeyRound } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { BRAND } from "../lib/brand";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PasswordInput } from "../components/ui/password-input";
import { Label } from "../components/ui/label";

const Shell = ({ children }) => (
  <div className="min-h-screen grid place-items-center p-6">
    <div className="w-full max-w-sm space-y-6 fade-up">
      <img src={BRAND.logoFull} alt={BRAND.name} className="h-20 w-auto mx-auto" />
      {children}
      <p className="text-sm text-center"><Link to="/login" className="text-primary font-semibold hover:underline" data-testid="back-to-login-link">Kembali ke halaman masuk</Link></p>
    </div>
  </div>
);

export const PW_HINT = "Minimal 8 karakter, berisi huruf dan angka.";
export const pwValid = (p) => p.length >= 8 && /[A-Za-z]/.test(p) && /\d/.test(p);

export function ForgotPassword() {
  const [id, setId] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.post("/auth/forgot-password", { identifier: id });
      setMsg(data.message);
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setLoading(false);
    }
  };
  return (
    <Shell>
      {msg ? (
        <div className="rounded-xl border bg-card p-5 text-center space-y-2" data-testid="forgot-success">
          <MailCheck className="h-10 w-10 mx-auto text-success" />
          <p className="text-sm">{msg}</p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          <div><h1 className="font-display text-2xl font-extrabold">Lupa Password</h1><p className="text-sm text-muted-foreground">Masukkan username atau email akun Anda (owner maupun kasir).</p></div>
          <div className="space-y-1.5"><Label>Username / Email</Label><Input required className="h-11" value={id} onChange={(e) => setId(e.target.value)} data-testid="forgot-identifier-input" /></div>
          {err && <div className="text-sm text-destructive" data-testid="forgot-error">{err}</div>}
          <Button type="submit" className="w-full h-11" disabled={loading} data-testid="forgot-submit-btn">{loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Kirim Link Reset</Button>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!pwValid(p1)) return setErr(PW_HINT);
    if (p1 !== p2) return setErr("Konfirmasi password tidak sama");
    setLoading(true);
    setErr("");
    try {
      await api.post("/auth/reset-password", { token: params.get("token") || "", password: p1 });
      setDone(true);
      setTimeout(() => nav("/login"), 2500);
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setLoading(false);
    }
  };
  return (
    <Shell>
      {done ? (
        <div className="rounded-xl border bg-card p-5 text-center space-y-2" data-testid="reset-success">
          <KeyRound className="h-10 w-10 mx-auto text-success" /><p className="text-sm">Password berhasil diubah. Silakan masuk dengan password baru.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
          <div><h1 className="font-display text-2xl font-extrabold">Buat Password Baru</h1><p className="text-sm text-muted-foreground">{PW_HINT}</p></div>
          <div className="space-y-1.5"><Label>Password Baru</Label><PasswordInput required className="h-11" value={p1} onChange={(e) => setP1(e.target.value)} data-testid="reset-password-input" /></div>
          <div className="space-y-1.5"><Label>Ulangi Password</Label><PasswordInput required className="h-11" value={p2} onChange={(e) => setP2(e.target.value)} data-testid="reset-password-confirm-input" /></div>
          {err && <div className="text-sm text-destructive" data-testid="reset-error">{err}</div>}
          <Button type="submit" className="w-full h-11" disabled={loading} data-testid="reset-submit-btn">{loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Simpan Password</Button>
        </form>
      )}
    </Shell>
  );
}
